import { useState } from 'react';
import { ChevronLeft, ChevronRight, Footprints, Loader2, RefreshCw } from 'lucide-react';
import { useI18n } from '@/shared/i18n';
import { RouteMap } from '../components/RouteMap';
import { useGuardRouteDetail, useGuardRoutes } from '../hooks';
import { formatDistance, formatDuration, formatPace, formatRouteDate } from '../routeFormat';

const inputClass =
  'px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

export const GuardRoutesPage = () => {
  const { t } = useI18n();
  const { routes, guards, filters, page, totalPages, loading, error, setPage, changeFilters, refresh } =
    useGuardRoutes();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Sin elección explícita (o si salió de la página actual) se muestra el más reciente.
  const activeId = routes.some((r) => r.id === selectedId) ? selectedId : routes[0]?.id ?? null;
  const detail = useGuardRouteDetail(activeId);
  const route = detail.route;
  const gapSeconds = route?.gaps.reduce((sum, gap) => sum + gap.durationSeconds, 0) ?? 0;

  const stat = (label: string, value: string) => (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-bold text-slate-800">{value}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">{t('guardRoutes.title')}</h2>
        <p className="text-slate-500 text-sm">{t('guardRoutes.subtitle')}</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          {t('guardRoutes.guard')}
          <select
            value={filters.guardId ?? ''}
            onChange={(e) => changeFilters({ ...filters, guardId: e.target.value || undefined })}
            className={inputClass}
          >
            <option value="">{t('guardRoutes.allGuards')}</option>
            {guards.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          {t('guardRoutes.from')}
          <input
            type="date"
            value={filters.dateFrom ?? ''}
            max={filters.dateTo}
            onChange={(e) => changeFilters({ ...filters, dateFrom: e.target.value || undefined })}
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          {t('guardRoutes.to')}
          <input
            type="date"
            value={filters.dateTo ?? ''}
            min={filters.dateFrom}
            onChange={(e) => changeFilters({ ...filters, dateTo: e.target.value || undefined })}
            className={inputClass}
          />
        </label>
        {(filters.guardId || filters.dateFrom || filters.dateTo) && (
          <button
            onClick={() => changeFilters({})}
            className="px-3 py-2 text-sm text-slate-600 rounded-lg hover:bg-slate-100"
          >
            {t('guardRoutes.clear')}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm">{error}</span>
          <button onClick={refresh} className="p-2 rounded-lg hover:bg-red-100" aria-label={t('guardRoutes.retry')}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 flex flex-col">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : routes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-slate-500">
              <Footprints className="w-8 h-8" />
              <p className="text-sm">{t('guardRoutes.empty')}</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {routes.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelectedId(r.id)}
                    className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${r.id === activeId ? 'bg-primary-50' : ''}`}
                  >
                    <p className="text-sm font-semibold text-slate-800">{r.guardName ?? t('guardRoutes.unknownGuard')}</p>
                    <p className="text-xs text-slate-500">{formatRouteDate(r.startedAt)}</p>
                    <p className="mt-1 text-sm text-slate-700">
                      {r.status === 'IN_PROGRESS' ? (
                        <span className="text-primary-600 font-medium">{t('guardRoutes.inProgress')}</span>
                      ) : (
                        <>
                          {formatDistance(r.distanceMeters)} · {formatDuration(r.durationSeconds)}
                        </>
                      )}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {totalPages > 1 && (
            <div className="mt-auto flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                aria-label={t('common.previous')}
                className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">{t('guardRoutes.page', { page, total: totalPages })}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages}
                aria-label={t('common.next')}
                className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 overflow-hidden">
          {detail.loading ? (
            <div className="flex items-center justify-center h-[520px]">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : detail.error ? (
            <div className="flex items-center justify-center h-[520px] text-sm text-red-600">{detail.error}</div>
          ) : route ? (
            <>
              <div className="p-4 space-y-3">
                <div>
                  <p className="font-semibold text-slate-800">{route.guardName ?? t('guardRoutes.unknownGuard')}</p>
                  <p className="text-xs text-slate-500">{formatRouteDate(route.startedAt)}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {stat(t('guardRoutes.distance'), formatDistance(route.distanceMeters))}
                  {stat(t('guardRoutes.pace'), formatPace(route.durationSeconds, route.distanceMeters))}
                  {stat(t('guardRoutes.duration'), formatDuration(route.durationSeconds))}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {stat(t('guardRoutes.stops'), String(route.stops.length))}
                  {stat(
                    t('guardRoutes.gaps'),
                    route.gaps.length > 0 ? `${route.gaps.length} (${formatDuration(gapSeconds)})` : '0',
                  )}
                  {stat(t('guardRoutes.points'), String(route.pointCount))}
                </div>
              </div>
              {/* isolate: los panes de Leaflet usan z-index de 400 a 1000; sin esto tapan menús y modales */}
              <div className="h-[420px] border-t border-slate-100 isolate">
                <RouteMap route={route} />
              </div>
            </>
          ) : (
            !loading && (
              <div className="flex items-center justify-center h-[520px] text-sm text-slate-500">
                {t('guardRoutes.selectRoute')}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
